import React, { CSSProperties, useState, useEffect } from "react";
import AccordionToggle from "../../Plate/AccordionToggle";
import CartesianArmMover from "../../Common/CartesianArmMover";
import { EArmType } from "../../../Model/Waypoint";
import Collapse from "../../Common/Collapse";
import Disabled from "../../Controls/Disabled";
import PsrCameraControl from "./PsrCameraControl";
import PsrCameraLiveImagePreferences from "./PsrCameraLiveImagePreferences";
import "./PsrCameraManualControl.css";

const ARM_MOVER = 0;
const CAMERA_CONTROL = 1;
const PREFERENCES = 2;
export const PSR_CAMERA_MANUAL_ACCORDION_STATE =
  "PSR_CAMERA_MANUAL_ACCORDION_STATE";

function getAccordionState(key: string): boolean[] {
  const value = localStorage.getItem(key);
  if (value != null) {
    return JSON.parse(value);
  } else {
    return [false, false, false];
  }
}

function getExpandAllAccordionState(key: string): boolean {
  const value = localStorage.getItem(key);
  if (value != null) {
    const accordionState: boolean[] = JSON.parse(value);
    return accordionState.some((val) => val === false);
  } else {
    return true;
  }
}

type Props = {
  psrIdentifier: string;
  psrCameraId: number;
  style?: React.CSSProperties;
  disabled: boolean;
  headerEnabled?: boolean;
  triggerExpandAll?: boolean; // use boolean state, true = trigger expand/collaps all
  setTriggerExpandAll?: (triiger: boolean) => void; // update the trigger boolean state
  onAllItemCollapsed?: (collapsed: boolean) => void; // called when all accordion collapsed or not
};

export default function PsrCameraManualControl({
  psrIdentifier,
  psrCameraId,
  disabled,
  style = {},
  headerEnabled = true,
  triggerExpandAll,
  setTriggerExpandAll = () => {},
  onAllItemCollapsed = () => {},
}: Props) {
  const [canExpandAll, setCanExpandAll] = useState(
    getExpandAllAccordionState(PSR_CAMERA_MANUAL_ACCORDION_STATE)
  );
  const [accordionState, setAccordionState] = useState(
    getAccordionState(PSR_CAMERA_MANUAL_ACCORDION_STATE)
  );

  useEffect(() => {
    localStorage.setItem(
      PSR_CAMERA_MANUAL_ACCORDION_STATE,
      JSON.stringify(accordionState)
    );
  }, [accordionState]);

  useEffect(() => {
    if (triggerExpandAll) {
      onExpandAllToggleClicked();
      setTriggerExpandAll(false);
    }
  }, [triggerExpandAll]);

  useEffect(() => {
    onAllItemCollapsed(canExpandAll);
  }, [canExpandAll]);

  const onAccordionToggleClicked = (id: number) => {
    const isOpening = !accordionState[id];
    let state;
    if (isOpening) {
      //collapse all panel
      state = [false, false, false];
      state[id] = true;
    } else {
      state = [...accordionState];
      state[id] = false;
    }
    //check if no panel expanded
    if (state.every((value) => value === false)) {
      setCanExpandAll(true);
    }
    if (state.every((value) => value === true)) {
      setCanExpandAll(false);
    }

    setAccordionState(state);
  };

  const onExpandAllToggleClicked = () => {
    if (!canExpandAll) {
      setAccordionState([false, false, false]);
      setCanExpandAll(true);
    } else {
      setAccordionState([true, true, true]);
      setCanExpandAll(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        height: "100%",
        overflow: "auto",
        ...style,
      }}
    >
      {headerEnabled && (
        <AccordionToggle
          doubleChevron
          expanded={!canExpandAll}
          onClick={() => onExpandAllToggleClicked()}
        >
          <div>Control Panel</div>
        </AccordionToggle>
      )}

      {/*--------ARM MOVER--------*/}
      <AccordionToggle
        expanded={accordionState[ARM_MOVER]}
        onClick={() => onAccordionToggleClicked(ARM_MOVER)}
      >
        Arm Mover
      </AccordionToggle>
      <Collapse expanded={accordionState[ARM_MOVER]}>
        <Disabled disabled={disabled}>
          <div className="storage-cal-arm-mover">
            <CartesianArmMover
              armType={EArmType.PsrArm}
              moduleIdentifier={psrIdentifier}
            />
          </div>
        </Disabled>
      </Collapse>

      {/*--------PSR CAMERA CONTROL--------*/}
      <AccordionToggle
        expanded={accordionState[CAMERA_CONTROL]}
        onClick={() => onAccordionToggleClicked(CAMERA_CONTROL)}
      >
        Camera Control
      </AccordionToggle>
      <Collapse expanded={accordionState[CAMERA_CONTROL]}>
        <Disabled disabled={disabled}>
          <PsrCameraControl
            psrIdentifier={psrIdentifier}
            psrCameraId={psrCameraId}
            disabled={disabled}
          />
        </Disabled>
      </Collapse>

      {/*-------PREFERENCES---------*/}
      <AccordionToggle
        expanded={accordionState[PREFERENCES]}
        onClick={() => onAccordionToggleClicked(PREFERENCES)}
      >
        Live Image Options
      </AccordionToggle>
      <Collapse expanded={accordionState[PREFERENCES]}>
        <div>
          <PsrCameraLiveImagePreferences
            psrIdentifier={psrIdentifier}
            psrCameraId={psrCameraId}
          />
        </div>
      </Collapse>
    </div>
  );
}
